import { Alert, Box, Button, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  FEE_AMOUNT_TYPES,
  FEE_CATEGORIES_BY_OPTION,
  FEE_FILTER_CATEGORIES,
  FEE_PAYMENT_OPTIONS,
  feeAmountLabel,
} from '@app/shared';
import type { FeeAmountType, FeePaymentOption, ProcessingFeeDto } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ConfirmDialog, FormDialog } from '../../components/dialogs';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { ForbiddenState } from '../../components/PageState';

/**
 * Payment Processing Fee Config (PC721), heading "Payment Processing Fee", as walked on the
 * reference (docs/telgoo5-reference/modules/12-tax-and-fees.md). The list's columns were not
 * seen (the tenant had no fee); they are this project's.
 */

const BASE = '/processing-fees';
interface RangeRow {
  from: string;
  to: string;
  amount: string;
  type: FeeAmountType;
}
const AMOUNT = /^\d{0,3}(\.\d{0,2})?$/;
const toUnits = (text: string) => Math.round(Number(text || 0) * 100);
const fromUnits = (value: number) => (value / 100).toFixed(2);

function FeeDialog({ fee, onClose }: { fee: ProcessingFeeDto | null; onClose: () => void }) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [setup, setSetup] = useState<string>(fee?.setup ?? '');
  const [option, setOption] = useState<FeePaymentOption | ''>(fee?.paymentOption ?? '');
  const [category, setCategory] = useState(fee?.paymentCategory ?? '');
  const [feeType, setFeeType] = useState(fee?.feeType ?? 'Fixed');
  const [amount, setAmount] = useState(fee?.feeType === 'Fixed' ? fromUnits(fee.amount) : '0');
  const [amountType, setAmountType] = useState<FeeAmountType>(fee?.amountType ?? 'Flat');
  const [ranges, setRanges] = useState<RangeRow[]>(
    fee?.ranges.map((r) => ({
      from: String(r.fromDollars),
      to: String(r.toDollars),
      amount: fromUnits(r.amount),
      type: r.amountType,
    })) ?? [{ from: '0', to: '', amount: '', type: 'Flat' }],
  );
  const [message, setMessage] = useState('');
  const save = useMutation({
    mutationFn: (body: unknown) =>
      fee
        ? api.put<ProcessingFeeDto>(`${BASE}/${fee.id}`, body)
        : api.post<ProcessingFeeDto>(BASE, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['processing-fees'] }),
  });

  const showAmount = setup === 'All' || (setup === 'Individual' && Boolean(category));
  const last = ranges[ranges.length - 1]!;
  // "Add More" checks the last row as the reference does before adding the next.
  const checkRow = (row: RangeRow): string => {
    if (!row.to) return 'Please input the range in Provide Range.';
    if (Number(row.from) >= Number(row.to)) return 'Invalid Range.';
    if (!row.amount) return 'Amount should not be blank.';
    if (row.type === 'Percentage' && Number(row.amount) > 100)
      return 'Value should be less then or equal to 100.';
    return '';
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setMessage('');
    if (!setup) return setMessage('Please select Processing Fee Setup.');
    if (setup === 'Individual' && !option) return setMessage('Please select Payment Option.');
    if (setup === 'Individual' && !category) return setMessage('Please select Payment Category.');
    if (feeType === 'Fixed') {
      if (!amount) return setMessage('Amount should not be blank.');
      if (amountType === 'Percentage' && Number(amount) > 100)
        return setMessage('Value should be less then or equal to 100.');
    } else {
      for (const row of ranges) {
        const problem = checkRow(row);
        if (problem) return setMessage(problem);
      }
    }
    try {
      await save.mutateAsync({
        setup,
        paymentOption: setup === 'Individual' ? option : '',
        paymentCategory: setup === 'Individual' ? category : '',
        feeType,
        ...(feeType === 'Fixed' ? { amount: toUnits(amount), amountType } : {}),
        ranges:
          feeType === 'Range'
            ? ranges.map((r) => ({
                fromDollars: Number(r.from),
                toDollars: Number(r.to),
                amount: toUnits(r.amount),
                amountType: r.type,
              }))
            : [],
      });
      notify.success(fee ? 'Processing fee updated.' : 'Processing fee added.');
      onClose();
    } catch (err) {
      if (err instanceof ApiError) setMessage(err.details[0]?.message ?? err.message);
      else notify.error(err);
    }
  };

  const amountInput = (
    value: string,
    onChange: (v: string) => void,
    type: FeeAmountType,
    onType: (t: FeeAmountType) => void,
    label: string,
  ) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <TextField
        required
        label={label}
        value={value}
        onChange={(e) => AMOUNT.test(e.target.value) && onChange(e.target.value)}
        slotProps={{ htmlInput: { maxLength: 5, inputMode: 'decimal' } }}
        sx={{ flex: 1 }}
      />
      <TextField
        select
        label={`${label} type`}
        value={type}
        onChange={(e) => onType(e.target.value as FeeAmountType)}
        sx={{ width: 150 }}
      >
        {FEE_AMOUNT_TYPES.map((t) => (
          <MenuItem key={t} value={t}>
            {t}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );

  return (
    <FormDialog
      open
      title={fee ? 'Edit Processing Fee' : 'Add Processing Fee'}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      submitting={save.isPending}
      maxWidth="md"
    >
      {message ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {message}
        </Alert>
      ) : null}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextField
          select
          required
          label="Processing Fee Setup"
          value={setup}
          onChange={(e) => {
            setSetup(e.target.value);
            setOption('');
            setCategory('');
            setFeeType('Fixed');
            setMessage('');
          }}
        >
          <MenuItem value="All">All</MenuItem>
          <MenuItem value="Individual">Individual</MenuItem>
        </TextField>
        {setup === 'Individual' ? (
          <>
            <TextField
              select
              required
              label="Payment Option"
              value={option}
              onChange={(e) => {
                setOption(e.target.value as FeePaymentOption);
                setCategory('');
                setFeeType('Fixed');
              }}
            >
              {FEE_PAYMENT_OPTIONS.map((o) => (
                <MenuItem key={o} value={o}>
                  {o}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              required
              label="Payment Category"
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                setFeeType('Fixed');
              }}
              disabled={!option}
            >
              {option ? <MenuItem value="All">All</MenuItem> : null}
              {(option ? FEE_CATEGORIES_BY_OPTION[option] : []).map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </>
        ) : null}
        {showAmount ? (
          <TextField
            select
            required
            label="Processing Fee Type"
            value={feeType}
            onChange={(e) => setFeeType(e.target.value as 'Fixed' | 'Range')}
          >
            <MenuItem value="Fixed">Fixed</MenuItem>
            <MenuItem value="Range">Range</MenuItem>
          </TextField>
        ) : null}
      </Box>
      {showAmount && feeType === 'Fixed' ? (
        <Box sx={{ mt: 2, maxWidth: 420 }}>
          {amountInput(amount, setAmount, amountType, setAmountType, 'Amount')}
        </Box>
      ) : null}
      {showAmount && feeType === 'Range' ? (
        <Box sx={{ mt: 2, display: 'grid', gap: 1.5 }} aria-label="Ranges">
          {ranges.map((row, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography sx={{ width: 110 }}>Provide Range</Typography>
              <TextField
                size="small"
                label="From"
                value={row.from}
                slotProps={{ htmlInput: { readOnly: true, maxLength: 4 } }}
                sx={{ width: 90 }}
              />
              <Typography>&lt;</Typography>
              <TextField
                size="small"
                label="To"
                value={row.to}
                onChange={(e) =>
                  /^\d{0,4}$/.test(e.target.value) &&
                  setRanges((rs) => rs.map((r, j) => (j === i ? { ...r, to: e.target.value } : r)))
                }
                slotProps={{
                  htmlInput: {
                    maxLength: 4,
                    inputMode: 'numeric',
                    readOnly: i < ranges.length - 1,
                  },
                }}
                sx={{ width: 90 }}
              />
              <Box sx={{ width: 300 }}>
                {amountInput(
                  row.amount,
                  (v) => setRanges((rs) => rs.map((r, j) => (j === i ? { ...r, amount: v } : r))),
                  row.type,
                  (t) => setRanges((rs) => rs.map((r, j) => (j === i ? { ...r, type: t } : r))),
                  `Amount ${i + 1}`,
                )}
              </Box>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => {
                const problem = checkRow(last);
                if (problem) return setMessage(problem);
                setMessage('');
                setRanges((rs) => [...rs, { from: last.to, to: '', amount: '', type: 'Flat' }]);
              }}
            >
              Add More
            </Button>
            <Button
              variant="outlined"
              disabled={ranges.length < 2}
              onClick={() => setRanges((rs) => rs.slice(0, -1))}
            >
              Delete
            </Button>
          </Box>
        </Box>
      ) : null}
    </FormDialog>
  );
}

/** Payment Processing Fee Config (PC721). */
export function ProcessingFeesPage() {
  const { can } = useAuth();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [typeDraft, setTypeDraft] = useState('');
  const [categoryDraft, setCategoryDraft] = useState('');
  const [filters, setFilters] = useState({ paymentType: '', paymentCategory: '' });
  const [editing, setEditing] = useState<ProcessingFeeDto | 'new' | null>(null);
  const [removing, setRemoving] = useState<ProcessingFeeDto | null>(null);
  const fees = useQuery({
    queryKey: ['processing-fees', filters],
    queryFn: () =>
      api.get<{ data: ProcessingFeeDto[] }>(BASE, {
        ...(filters.paymentType ? { paymentType: filters.paymentType } : {}),
        ...(filters.paymentCategory ? { paymentCategory: filters.paymentCategory } : {}),
      }),
    select: (r) => r.data,
    enabled: can('PC721'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`${BASE}/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['processing-fees'] }),
  });
  if (!can('PC721')) return <ForbiddenState code="PC721" />;

  const categoryChoices =
    typeDraft && typeDraft !== 'All'
      ? FEE_CATEGORIES_BY_OPTION[typeDraft as FeePaymentOption]
      : FEE_FILTER_CATEGORIES;
  const columns: Column<ProcessingFeeDto>[] = [
    { key: 'setup', header: 'Processing Fee Setup', render: (f) => f.setup },
    { key: 'option', header: 'Payment Type', render: (f) => f.paymentOption || 'All' },
    { key: 'category', header: 'Payment Category', render: (f) => f.paymentCategory || 'All' },
    { key: 'type', header: 'Processing Fee Type', render: (f) => f.feeType },
    {
      key: 'amount',
      header: 'Amount',
      render: (f) =>
        f.feeType === 'Fixed'
          ? feeAmountLabel(f.amount, f.amountType)
          : f.ranges
              .map(
                (r) =>
                  `${r.fromDollars} - ${r.toDollars}: ${feeAmountLabel(r.amount, r.amountType)}`,
              )
              .join('; '),
    },
    {
      key: 'action',
      header: 'Action',
      render: (f) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button size="small" onClick={() => setEditing(f)}>
            Edit
          </Button>
          <Button size="small" color="error" onClick={() => setRemoving(f)}>
            Remove
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader title="Payment Processing Fee" code="PC721" />
      <Button component={Link} to="/settings/other" sx={{ mb: 1 }}>
        Other Settings
      </Button>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
        <Box
          component="form"
          aria-label="Processing fee filters"
          onSubmit={(e) => {
            e.preventDefault();
            setFilters({ paymentType: typeDraft, paymentCategory: categoryDraft });
          }}
          sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <TextField
            select
            size="small"
            label="Payment Type"
            value={typeDraft}
            onChange={(e) => {
              setTypeDraft(e.target.value);
              setCategoryDraft('');
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Select All</MenuItem>
            <MenuItem value="All">All</MenuItem>
            {FEE_PAYMENT_OPTIONS.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Payment Category"
            value={categoryDraft}
            onChange={(e) => setCategoryDraft(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Select All</MenuItem>
            {typeDraft !== '' ? <MenuItem value="All">All</MenuItem> : null}
            {categoryChoices.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <Button type="submit" variant="outlined">
            Submit
          </Button>
        </Box>
      </Paper>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ flexGrow: 1 }}>Search Results : {fees.data?.length ?? 0}</Typography>
        <Button variant="contained" onClick={() => setEditing('new')}>
          Add Processing Fee
        </Button>
      </Box>
      <DataTable
        label="Processing fees"
        columns={columns}
        rows={fees.data}
        rowKey={(f) => f.id}
        loading={fees.isFetching}
        error={fees.error}
        onRetry={() => void fees.refetch()}
        emptyText="No Record Found!!"
      />
      {editing ? (
        <FeeDialog fee={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
      <ConfirmDialog
        open={Boolean(removing)}
        title="Remove processing fee"
        message="Are you sure you want to remove this processing fee?"
        confirmLabel="Remove"
        danger
        busy={remove.isPending}
        onClose={() => setRemoving(null)}
        onConfirm={() =>
          removing &&
          remove.mutate(removing.id, {
            onSuccess: () => {
              notify.success('Processing fee removed.');
              setRemoving(null);
            },
            onError: (err) => notify.error(err),
          })
        }
      />
    </Box>
  );
}
